/** @jsxImportSource @emotion/react */
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { FormikErrors, useFormik } from 'formik';
import { useMerchantApplicationContext } from 'contexts';
import { StepComponentProps } from 'types/interfaces/sign-up/MerchantApplication.types';
import { ActivationStepKey } from 'modules/activation/ActivationConfig';
import {
  getActivationRadioOptions,
  getInitialActivationStepData,
} from 'modules/activation/steps/activationStepData';

type ActivationProvidedServicesProps = {
  providesService?: boolean;
  fullPaymentTakeTime?: string;
};
import { ActivationRadioGroup } from '../ActivationRadioGroup';
import * as styles from 'modules/activation/ActivationFlow.styles';

const emptyValues: ActivationProvidedServicesProps = {
  providesService: undefined,
  fullPaymentTakeTime: '',
};

const validateValues = (
  values: ActivationProvidedServicesProps
): FormikErrors<ActivationProvidedServicesProps> => {
  const errors: FormikErrors<ActivationProvidedServicesProps> = {};

  if (values.providesService === undefined) {
    errors.providesService = 'Please select an option';
  }

  if (values.providesService && !values.fullPaymentTakeTime) {
    errors.fullPaymentTakeTime = 'Please select when full payment is taken';
  }

  return errors;
};

export const ProvidedServicesStepModule: FC<StepComponentProps> = ({
  provideSubmit,
}) => {
  const { activationData, getActivationStepData, setActivationStepData } =
    useMerchantApplicationContext();
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const savedValuesRef = useRef(
    getActivationStepData(ActivationStepKey.ProvidedServices)
  );

  const initialValues = useMemo(
    () => ({
      ...emptyValues,
      ...getInitialActivationStepData(
        ActivationStepKey.ProvidedServices,
        activationData
      ),
      ...(savedValuesRef.current ?? {}),
    }),
    [activationData]
  );

  const fullPaymentOptions = getActivationRadioOptions('fullPaymentTakeTime');

  const formik = useFormik<ActivationProvidedServicesProps>({
    initialValues,
    validate: validateValues,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      setActivationStepData(ActivationStepKey.ProvidedServices, values, true);
    },
  });

  useEffect(() => {
    if (submitAttempted) {
      formik.validateForm();
    }

    setActivationStepData(
      ActivationStepKey.ProvidedServices,
      formik.values,
      true
    );
  }, [formik.values, submitAttempted, setActivationStepData]);

  useEffect(() => {
    provideSubmit?.(async () => {
      setSubmitAttempted(true);

      const errors = await formik.validateForm();

      if (Object.keys(errors).length > 0) {
        formik.setTouched(
          {
            providesService: true,
            fullPaymentTakeTime: true,
          },
          false
        );
        return false;
      }

      await formik.submitForm();
      return true;
    });
  }, [provideSubmit, formik]);

  const handleProvidesServiceChange = async (value: boolean): Promise<void> => {
    await formik.setFieldValue('providesService', value);

    if (!value) {
      await formik.setFieldValue('fullPaymentTakeTime', '');
    }
  };

  return (
    <>
      {/* MC-4775 Remove this hardcoded div for progressbar, and parent <>, so it should render from <form> tag */}
      <div css={styles.progressTrack}>
        <div css={styles.progressFill(36)} />
      </div>
      <form css={styles.form}>
        <ActivationRadioGroup
          name="providesService"
          label="Does your business provide a service?"
          value={formik.values.providesService}
          onChange={handleProvidesServiceChange}
          error={submitAttempted && Boolean(formik.errors.providesService)}
          helperText={
            submitAttempted ? formik.errors.providesService : undefined
          }
        />

        {formik.values.providesService && (
          <ActivationRadioGroup
            name="fullPaymentTakeTime"
            label="When is payment taken?"
            options={fullPaymentOptions}
            value={formik.values.fullPaymentTakeTime}
            onChange={(value) =>
              formik.setFieldValue('fullPaymentTakeTime', value)
            }
            error={
              submitAttempted && Boolean(formik.errors.fullPaymentTakeTime)
            }
            helperText={
              submitAttempted ? formik.errors.fullPaymentTakeTime : undefined
            }
          />
        )}
      </form>
    </>
  );
};

export default ProvidedServicesStepModule;
